import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useT } from '@/store';
import { eurExact } from '@/lib/format';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { qk } from '@/lib/queryKeys';

/**
 * V-87. Whether the price list has anything to say about this load, at the
 * moment the load is agreed rather than five weeks later.
 *
 * Both screens that create work carry a sentence saying that leaving the
 * agreed rate blank means the price list decides — `orders.agreedPriceHint`
 * and `approvals.priceHint`. Neither had ever checked whether it has
 * anything to decide with.
 *
 * Measured on a live server: the quarry had ZERO price-list rows;
 * POST /api/orders took a 24 t load of "Skalda 0/32" with no agreed rate
 * and warned about nothing; after delivery the invoice draft put it in
 * `excluded.unpriced` with amount_eur null, lines [] and net_eur 0. The
 * diesel is burned, the driver is paid, and there is no invoice to send.
 *
 * IT NEVER BLOCKS. A carrier may knowingly haul a load he means to price
 * afterwards, and a form that refused would be worked around within a day.
 * What he must not do is find out from the unbilled panel in September.
 *
 * SILENT ABOUT A MISSING LIST RATE THE MOMENT A RATE IS TYPED — and V-106
 * narrowed that heading, which used to read "silent the moment a rate is
 * typed" and stopped being true of this file the day V-106 landed.
 *
 * The agreed rate beats the price list (core/invoicing.py's COALESCE), so a
 * load that carries one is billable whatever the list holds, and a warning
 * about a missing list rate left on screen beside a filled field is a
 * warning about nothing — which is how the next one gets ignored.
 *
 * V-106 added the one thing a typed rate does NOT settle: whether it covers
 * what the stone costs. That question only exists once a rate has been
 * typed, so the query stays enabled and a red note appears when the rate is
 * at or below cost_per_ton. Everything else on this component still goes
 * quiet, which is why the heading needed the qualifier rather than a
 * deletion.
 *
 * The list of names the quarry IS priced for is the actionable half. A load
 * typed "Skalda 0/32" against a list that says "Skalda" is a five-second
 * fix and an invisible one, because the two names look alike to a reader
 * and not at all alike to core/utils.material_key().
 */
export function ListPriceNotice({ quarryId, cargoType, agreedPrice }) {
  const t = useT();
  // V-90. The cargo field is TYPED, not picked, and the material is part of
  // the question — so every keystroke was a new query key and a new request.
  // Measured in a browser: typing "Skalda 0/32" fired TEN requests, nine of
  // them asking about a fragment ("S", "Sk", "Ska") that no price list holds
  // — and each of those nine answers rendered the red warning, so the line
  // strobed under the field while the dispatcher was still typing the name.
  // A warning that appears before the question is finished is one a
  // dispatcher learns to look past, which is the failure this whole notice
  // was written to avoid.
  //
  // The same helper the search inputs use, and the same 300ms: below a
  // typist's inter-key gap, above the point where the answer feels like it
  // lags the typing. Debounced BEFORE the trim so a trailing space typed
  // mid-word does not restart anything.
  const cargo = useDebouncedValue(String(cargoType ?? ''), 300).trim();
  const typedRate = String(agreedPrice ?? '').trim() !== '';

  const { data } = useQuery({
    queryKey: qk.quarryPriceCheck(quarryId, cargo),
    queryFn: () => api(`/api/quarries/${quarryId}/price-check?cargo_type=${encodeURIComponent(cargo)}`),
    // Not asked until there is a question: no quarry or no material means
    // there is nothing to look up.
    //
    // V-106 removed `&& !typedRate`. A typed rate used to end the question,
    // and for the missing-list-price half it does — a dispatcher who named a
    // rate does not need to be told the list is silent. But the answer also
    // carries what the QUARRY charges, and a typed rate is exactly when that
    // becomes worth knowing. The query key is (quarry, material) and does
    // not include the rate, so typing one refetches nothing.
    enabled: Boolean(quarryId) && cargo !== '',
    staleTime: 60_000,
  });

  if (!data) return null;

  // V-106. A rate that does not cover the purchase price of the stone.
  //
  // quarry_prices.cost_per_ton is what this carrier PAYS for the material;
  // the agreed rate is what they charge the client for it. Below the first,
  // the load loses money before a kilometre is driven — and today the only
  // place that arithmetic ever happens is the net-margin tile a month later,
  // where it sits mixed with fuel, tolls and empty running.
  //
  // NULL for a carrier who only hauls, which is most of them, and then this
  // never fires. Not blocked, and the wording says the fact rather than the
  // verdict: selling a material at cost to keep a client is a decision a
  // dispatcher is allowed to make, and one this product cannot second-guess.
  const rate = Number(String(agreedPrice ?? '').replace(',', '.'));
  // `rate > 0` is the load-bearing half: a field holding "0" is a rate
  // half-typed on the way to "9,60", and a warning that appears at the first
  // keystroke is one a dispatcher learns to look past — the failure V-90
  // fixed for the other branch of this same notice. It also makes the null
  // check above unobservable, since blueprints/quarries.py refuses a
  // cost_per_ton of 0 or less, so a stored cost is either NULL or positive
  // and `rate <= null` can never be reached from a positive rate. The check
  // stays for what it says, not for what it does.
  if (typedRate && data.cost_per_ton != null && Number.isFinite(rate)
      && rate > 0 && rate <= data.cost_per_ton) {
    return (
      <p role="note" className="text-[10.5px] font-semibold leading-snug text-[#FCA5A5]">
        {t('orders.rateBelowCost', {
          material: data.material_name ?? cargo,
          cost: eurExact(data.cost_per_ton),
          agreed: eurExact(rate),
        })}
      </p>
    );
  }

  if (typedRate) return null;
  if (data.price_per_ton != null) return null;

  const known = data.known_materials ?? [];
  return (
    <p role="note" className="text-[10.5px] leading-snug text-amber">
      {t('orders.noListPrice', { quarry: data.quarry_name ?? '', material: cargo })}
      {' '}
      {known.length > 0
        ? t('orders.noListPriceKnown', { names: known.join(', ') })
        : t('orders.noListPriceEmpty')}
    </p>
  );
}
